/* eslint-disable */
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  HStack,
  Icon,
  Box,
  Input,
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningTwoIcon, InfoIcon, QuestionIcon } from '@chakra-ui/icons';

const ModalToastContext = createContext();

export const useModalToast = () => useContext(ModalToastContext);

export const ModalToastProvider = ({ children }) => {
  // --- Toast (alert replacement) ---
  const [isOpen, setIsOpen] = useState(false);
  const [toastOptions, setToastOptions] = useState({
    title: '',
    description: '',
    status: 'info',
  });

  // --- Confirm dialog ---
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmOptions, setConfirmOptions] = useState({
    title: '',
    description: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    status: 'warning',
  });
  const confirmResolveRef = useRef(null);

  // --- Prompt dialog ---
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptOptions, setPromptOptions] = useState({
    title: '',
    description: '',
    placeholder: '',
    confirmText: 'Submit',
    cancelText: 'Cancel',
    status: 'info',
  });
  const [promptValue, setPromptValue] = useState('');
  const promptResolveRef = useRef(null);

  // ========== TOAST (alert replacement) ==========
  const toast = useCallback((options) => {
    setToastOptions(options);
    setIsOpen(true);
    if (options.duration && options.duration > 0) {
      setTimeout(() => setIsOpen(false), options.duration);
    }
  }, []);

  const handleClose = () => setIsOpen(false);

  // ========== CONFIRM (window.confirm replacement) ==========
  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      confirmResolveRef.current = resolve;
      setConfirmOptions({
        title: typeof options === 'string' ? options : options.title || 'Confirm',
        description: typeof options === 'string' ? '' : options.description || '',
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        status: options.status || 'warning',
      });
      setConfirmOpen(true);
    });
  }, []);

  const handleConfirmYes = () => {
    setConfirmOpen(false);
    confirmResolveRef.current?.(true);
  };
  const handleConfirmNo = () => {
    setConfirmOpen(false);
    confirmResolveRef.current?.(false);
  };

  // ========== PROMPT (window.prompt replacement) ==========
  const prompt = useCallback((options) => {
    return new Promise((resolve) => {
      promptResolveRef.current = resolve;
      setPromptValue('');
      setPromptOptions({
        title: typeof options === 'string' ? options : options.title || 'Input Required',
        description: typeof options === 'string' ? '' : options.description || '',
        placeholder: options.placeholder || '',
        confirmText: options.confirmText || 'Submit',
        cancelText: options.cancelText || 'Cancel',
        status: options.status || 'info',
      });
      setPromptOpen(true);
    });
  }, []);

  const handlePromptSubmit = () => {
    setPromptOpen(false);
    promptResolveRef.current?.(promptValue);
  };
  const handlePromptCancel = () => {
    setPromptOpen(false);
    promptResolveRef.current?.(null);
  };

  // Map status to icons and colors
  const getStatusConfig = (status) => {
    switch (status) {
      case 'success':
        return { icon: CheckCircleIcon, color: 'green.500', bg: 'green.50' };
      case 'error':
        return { icon: WarningTwoIcon, color: 'red.500', bg: 'red.50' };
      case 'warning':
        return { icon: WarningTwoIcon, color: 'orange.500', bg: 'orange.50' };
      case 'confirm':
        return { icon: QuestionIcon, color: 'purple.500', bg: 'purple.50' };
      default:
        return { icon: InfoIcon, color: 'blue.500', bg: 'blue.50' };
    }
  };

  const config = getStatusConfig(toastOptions.status);
  const StatusIcon = config.icon;

  const confirmConfig = getStatusConfig(confirmOptions.status);
  const ConfirmIcon = confirmConfig.icon;

  const promptConfig = getStatusConfig(promptOptions.status);
  const PromptIcon = promptConfig.icon;

  return (
    <ModalToastContext.Provider value={{ toast, confirm, prompt }}>
      {children}

      {/* ======= TOAST MODAL (alert replacement) ======= */}
      <Modal isOpen={isOpen} onClose={handleClose} isCentered size="sm">
        <ModalOverlay backdropFilter="blur(3px)" bg="blackAlpha.300" />
        <ModalContent borderRadius="2xl" boxShadow="xl" overflow="hidden">
          <Box h="6px" w="full" bg={config.color} />
          <ModalBody py={6}>
            <VStack spacing={4} align="center" textAlign="center">
              <Box p={3} borderRadius="full" bg={config.bg}>
                <Icon as={StatusIcon} w={8} h={8} color={config.color} />
              </Box>
              <Box>
                <Text fontWeight="800" fontSize="lg" color="gray.800" mb={1}>
                  {toastOptions.title}
                </Text>
                {toastOptions.description && (
                  <Text fontSize="sm" color="gray.500" fontWeight="500">
                    {toastOptions.description}
                  </Text>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter justifyContent="center" pb={6}>
            <Button
              bg={config.color}
              color="white"
              _hover={{ opacity: 0.9 }}
              borderRadius="xl"
              onClick={handleClose}
              px={8}
            >
              Acknowledge
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ======= CONFIRM MODAL (window.confirm replacement) ======= */}
      <Modal isOpen={confirmOpen} onClose={handleConfirmNo} isCentered size="sm">
        <ModalOverlay backdropFilter="blur(3px)" bg="blackAlpha.300" />
        <ModalContent borderRadius="2xl" boxShadow="xl" overflow="hidden">
          <Box h="6px" w="full" bg={confirmConfig.color} />
          <ModalBody py={6}>
            <VStack spacing={4} align="center" textAlign="center">
              <Box p={3} borderRadius="full" bg={confirmConfig.bg}>
                <Icon as={ConfirmIcon} w={8} h={8} color={confirmConfig.color} />
              </Box>
              <Box>
                <Text fontWeight="800" fontSize="lg" color="gray.800" mb={1}>
                  {confirmOptions.title}
                </Text>
                {confirmOptions.description && (
                  <Text fontSize="sm" color="gray.500" fontWeight="500">
                    {confirmOptions.description}
                  </Text>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter justifyContent="center" pb={6}>
            <HStack spacing={3}>
              <Button
                variant="outline"
                borderRadius="xl"
                onClick={handleConfirmNo}
                px={6}
                borderColor="gray.300"
              >
                {confirmOptions.cancelText}
              </Button>
              <Button
                bg={confirmConfig.color}
                color="white"
                _hover={{ opacity: 0.9 }}
                borderRadius="xl"
                onClick={handleConfirmYes}
                px={6}
              >
                {confirmOptions.confirmText}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ======= PROMPT MODAL (window.prompt replacement) ======= */}
      <Modal isOpen={promptOpen} onClose={handlePromptCancel} isCentered size="sm">
        <ModalOverlay backdropFilter="blur(3px)" bg="blackAlpha.300" />
        <ModalContent borderRadius="2xl" boxShadow="xl" overflow="hidden">
          <Box h="6px" w="full" bg={promptConfig.color} />
          <ModalBody py={6}>
            <VStack spacing={4} align="center" textAlign="center">
              <Box p={3} borderRadius="full" bg={promptConfig.bg}>
                <Icon as={PromptIcon} w={8} h={8} color={promptConfig.color} />
              </Box>
              <Box>
                <Text fontWeight="800" fontSize="lg" color="gray.800" mb={1}>
                  {promptOptions.title}
                </Text>
                {promptOptions.description && (
                  <Text fontSize="sm" color="gray.500" fontWeight="500" whiteSpace="pre-line">
                    {promptOptions.description}
                  </Text>
                )}
              </Box>
              <Input
                placeholder={promptOptions.placeholder}
                value={promptValue}
                onChange={(e) => setPromptValue(e.target.value)}
                borderRadius="xl"
                textAlign="center"
                fontWeight="600"
                onKeyDown={(e) => { if (e.key === 'Enter') handlePromptSubmit(); }}
              />
            </VStack>
          </ModalBody>
          <ModalFooter justifyContent="center" pb={6}>
            <HStack spacing={3}>
              <Button
                variant="outline"
                borderRadius="xl"
                onClick={handlePromptCancel}
                px={6}
                borderColor="gray.300"
              >
                {promptOptions.cancelText}
              </Button>
              <Button
                bg={promptConfig.color}
                color="white"
                _hover={{ opacity: 0.9 }}
                borderRadius="xl"
                onClick={handlePromptSubmit}
                px={6}
              >
                {promptOptions.confirmText}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </ModalToastContext.Provider>
  );
};
